import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, merge } from 'rxjs';
import { catchError, concatMap, map, switchMap, scan, startWith, shareReplay } from 'rxjs/operators';
import { environment } from '@env/environment';
import { StorageService } from '@services';
import { ReportingProvider } from '../..';

@Injectable()
export class JourneyProvider {

    constructor(private http: HttpClient, private reportingProvider: ReportingProvider, private storageService: StorageService) { }

    get(companyId: number | string, journeyId: number, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}`, { params }).pipe(
            concatMap((data) => {
                if (params?.page > 1) {
                    return of(data);
                } else {
                    return this.reportingProvider.getContentSubscriptionOverview(this.storageService.get('company').id, journeyId, [this.storageService.get('me').id], 'traject').pipe(
                        map((res) => ({ ...data, session: res[this.storageService.get('me').id]?.context })),
                        catchError(() => of(data)));
                }
            }
            ),
            concatMap((data: any) => this.reportingProvider.getContentSubscriptions(this.storageService.get('company').id, data.contents.map(content => ({ context: content.context, context_id: content.context === 'course' ? content.subscriptionCourseId : content.contextId })), this.storageService.get('me').id).pipe(
                map((res) => {
                    data.contents = data.contents.map((content) => {
                        const stats = res.filter(item => ((content.context === 'course' || content.context === 'external_content') ? item.context.contextId : item.context.sectionContentId) === (content.context === 'course' ? content.subscriptionCourseId : content.contextId))[0];
                        content.session = { ...content.session, ...{ eventDuration: 0, duration: 0, score: 0 }, ...stats?.context, ...{ progress: content.progress ?? 0 } }

                        return content;
                    })
                    return data;
                }),
                catchError(() => of(data)))
            )
        );
    }

    /** Respuesta rápida de `GET traject/{id}` -- sin `is_locked` (ver `with_lock_state=0`). */
    getBase(companyId: number | string, journeyId: number, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}`, {
            params: { ...(params || {}), with_lock_state: 0 }
        });
    }

    /** `is_locked` por contenido, para la MISMA página que `getBase` -- reenviar el mismo `page`/`per_page`. */
    getLockState(companyId: number | string, journeyId: number, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}/contents/lock-state`, { params });
    }

    /**
     * Versión dividida de `get()`, pensada para no bloquear la navegación (ver `JourneyResolver`).
     * Combina 4 fuentes en paralelo -- `getBase` (rápida), `overview` (no depende de `base`, hoy
     * se paralelizó porque el `concatMap` de `get()` la serializaba sin motivo), `sessions`
     * (depende de `base.contents`, vía `switchMap`) y `lock` (la parte cara, ~segundos) -- con un
     * `scan` que acumula por clave en vez de un merge incremental, para que el orden real de
     * llegada (que puede variar) no pise datos parciales.
     */
    getDetail(companyId: number | string, journeyId: number, params?: any): Observable<any> {
        const me = this.storageService.get('me');
        const company = this.storageService.get('company');

        const base$ = this.getBase(companyId, journeyId, params).pipe(shareReplay(1));

        const overview$ = params?.page > 1
            ? of(null)
            : this.reportingProvider.getContentSubscriptionOverview(company.id, journeyId, [me.id], 'traject').pipe(
                catchError(() => of(null)));

        const sessions$ = base$.pipe(
            switchMap((data: any) => this.reportingProvider.getContentSubscriptions(
                company.id,
                data.contents.map(content => ({
                    context: content.context,
                    context_id: content.context === 'course' ? content.subscriptionCourseId : content.contextId
                })),
                me.id
            )),
            catchError(() => of([])));

        const lock$ = this.getLockState(companyId, journeyId, params).pipe(catchError(() => of(null)));

        return merge(
            base$.pipe(map((d) => ({ k: 'base', d }))),
            overview$.pipe(map((d) => ({ k: 'overview', d }))),
            sessions$.pipe(map((d) => ({ k: 'sessions', d }))),
            lock$.pipe(map((d) => ({ k: 'lock', d }))),
        ).pipe(
            scan((acc: any, e: any) => ({ ...acc, [e.k]: e.d }), {}),
            map((acc: any) => this.composeJourney(journeyId, acc)),
            startWith(this.skeletonJourney(journeyId)),
            shareReplay(1),
        );
    }

    private composeJourney(journeyId: number, acc: any): any {
        if (!acc.base) {
            return this.skeletonJourney(journeyId);
        }

        const lockById = new Map((acc.lock || []).map((l: any) => [l.id, l.isLocked]));
        const sessions: any[] = acc.sessions || [];

        const contents = (acc.base.contents || []).map((content: any) => {
            const stats = sessions.filter(item => (
                (content.context === 'course' || content.context === 'external_content')
                    ? item.context.contextId
                    : item.context.sectionContentId
            ) === (content.context === 'course' ? content.subscriptionCourseId : content.contextId))[0];

            return {
                ...content,
                isSkeleton: false,
                isLocked: lockById.has(content.id) ? lockById.get(content.id) : content.isLocked,
                session: { ...content.session, ...{ eventDuration: 0, duration: 0, score: 0 }, ...stats?.context, ...{ progress: content.progress ?? 0 } }
            };
        });

        return {
            ...acc.base,
            isSkeleton: false,
            lockStatePending: !acc.lock,
            session: acc.overview ? acc.overview[this.storageService.get('me').id]?.context : acc.base.session,
            contents
        };
    }

    private skeletonJourney(journeyId: number): any {
        return {
            id: journeyId,
            isSkeleton: true,
            lockStatePending: true,
            title: '',
            description: '',
            picture: null,
            progress: 0,
            subscribed: false,
            subscribable: false,
            contactIds: [],
            pagination: null,
            contents: Array.from({ length: 5 }, (_, i) => ({
                id: -(i + 1),
                isSkeleton: true,
                isLocked: null,
                context: 'course',
                data: {}
            }))
        };
    }

    getCorrections(companyId: number | string, journeyId: number, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}/corrections`, { params }).pipe(
            map((data) => data)
        );
    }

    unsubscribe(id: number, params?: any): Observable<any> {
        return this.http.delete(`${environment.envVar.API_URL}/subscriptions/trajects/${id}`, { params }).pipe(
            map((data) => data)
        );
    }

    resumeJourney(companyId: number, journeyId: number, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}/continue`, { params }).pipe(
            map((data) => data)
        );
    }

    getTemplates(companyId: number, journeyId: number, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}/instances`, { params }).pipe(
            map((data) => data)
        );
    }

    getEvents(companyId: number, journeyId: number, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}/events`, { params }).pipe(
            map((data) => data)
        );
    }

    subscribe(companyId: number, journeyId: number, params?: any): Observable<any> {
        return this.http.post(`${environment.envVar.API_URL}/company/${companyId}/traject/${journeyId}/users`, params).pipe(
            map((data) => data)
        );
    }
}
